import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type PointerEvent as PointerEventReact, type WheelEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, X } from 'lucide-react';
import { Botao } from './base';

/**
 * Tela de entrada (30/10): ferramenta de ajuste da imagem antes do envio, sem dependência nova (canvas do navegador).
 *  - Recorte com proporção travada: 16:9 no fundo; na logo, livre de 1:1 a 5:1 (o controle "Proporção").
 *  - Arrastar (ponteiro) ou setas do teclado movem; zoom pelo controle, pela roda do mouse ou por + e -.
 *  - "Usar este recorte" desenha no canvas no tamanho final (fundo 1920 × 1080; logo até 800 × 240, sem ampliar) e comprime:
 *    fundo em WebP (JPEG onde o navegador não gera WebP) baixando a qualidade até caber em ~500 KB; logo em WebP ou PNG, com
 *    a transparência. O servidor ainda confere e reprocessa (tira metadados, gera as variantes).
 * A matemática do recorte é pura (exportada) e testada sem navegador.
 */

export type ModoRecorte = 'fundo' | 'logo' | 'avatar';
export interface Recorte {
  x: number;
  y: number;
  largura: number;
  altura: number;
}

export const PROPORCAO_FUNDO = 16 / 9;
export const PROPORCAO_LOGO = { min: 1, max: 5 } as const;
export const SAIDA_FUNDO = { largura: 1920, altura: 1080 } as const;
export const SAIDA_LOGO_MAX = { largura: 800, altura: 240 } as const;
export const ALVO_FUNDO_BYTES = 500 * 1024;
/** Lista de 30/10 (item 6): a foto do perfil é um quadrado de 512 px (o servidor refaz em 96 e 256), guiado por um círculo. */
export const PROPORCAO_AVATAR = 1;
export const SAIDA_AVATAR = { largura: 512, altura: 512 } as const;
export const ALVO_AVATAR_BYTES = 150 * 1024;
export const ZOOM_MAX = 4;

const limitar = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

/** O maior retângulo da proporção dada que cabe na imagem, no centro dela (zoom 1). */
export function recorteInicial(largura: number, altura: number, proporcao: number): Recorte {
  const l = Math.min(largura, altura * proporcao);
  const a = l / proporcao;
  return { x: (largura - l) / 2, y: (altura - a) / 2, largura: l, altura: a };
}

/** O recorte com o zoom dado (1 = o maior possível), mantendo o centro do recorte atual e sem sair da imagem. */
export function recorteComZoom(atual: Recorte, largura: number, altura: number, proporcao: number, zoom: number): Recorte {
  const base = recorteInicial(largura, altura, proporcao);
  const z = limitar(zoom, 1, ZOOM_MAX);
  const l = base.largura / z;
  const a = base.altura / z;
  const cx = atual.x + atual.largura / 2;
  const cy = atual.y + atual.altura / 2;
  return { x: limitar(cx - l / 2, 0, largura - l), y: limitar(cy - a / 2, 0, altura - a), largura: l, altura: a };
}

/** Move o recorte em pixels da imagem, sem sair dela. */
export function moverRecorte(r: Recorte, dx: number, dy: number, largura: number, altura: number): Recorte {
  return { ...r, x: limitar(r.x + dx, 0, largura - r.largura), y: limitar(r.y + dy, 0, altura - r.altura) };
}

/** Tamanho final: o fundo sempre 1920 × 1080; a foto do perfil 512 × 512; a logo cabe em 800 × 240 sem ampliar. */
export function tamanhoDaSaida(modo: ModoRecorte, r: Recorte): { largura: number; altura: number } {
  if (modo === 'fundo') return { ...SAIDA_FUNDO };
  if (modo === 'avatar') return { ...SAIDA_AVATAR };
  const escala = Math.min(1, SAIDA_LOGO_MAX.largura / r.largura, SAIDA_LOGO_MAX.altura / r.altura);
  return { largura: Math.max(1, Math.round(r.largura * escala)), altura: Math.max(1, Math.round(r.altura * escala)) };
}

const paraBlob = (c: HTMLCanvasElement, tipo: string, qualidade?: number) => new Promise<Blob | null>((ok) => c.toBlob(ok, tipo, qualidade));

/** Desenha o recorte no tamanho final e comprime (ver o comentário do arquivo). */
export async function exportarRecorte(fonte: CanvasImageSource, r: Recorte, modo: ModoRecorte, criarCanvas: () => HTMLCanvasElement = () => document.createElement('canvas')): Promise<Blob> {
  const { largura, altura } = tamanhoDaSaida(modo, r);
  const c = criarCanvas();
  c.width = largura;
  c.height = altura;
  const g = c.getContext('2d');
  if (!g) throw new Error('canvas indisponível');
  g.imageSmoothingEnabled = true;
  g.imageSmoothingQuality = 'high';
  g.clearRect(0, 0, largura, altura);
  g.drawImage(fonte, r.x, r.y, r.largura, r.altura, 0, 0, largura, altura);
  if (modo === 'logo') {
    const webp = await paraBlob(c, 'image/webp', 0.92);
    if (webp && webp.type === 'image/webp') return webp;
    const png = await paraBlob(c, 'image/png');
    if (!png) throw new Error('canvas sem saída');
    return png;
  }
  let ultimo: Blob | null = null;
  const alvo = modo === 'avatar' ? ALVO_AVATAR_BYTES : ALVO_FUNDO_BYTES;
  for (const q of [0.85, 0.78, 0.7, 0.62, 0.55]) {
    const webp = await paraBlob(c, 'image/webp', q);
    const b = webp && webp.type === 'image/webp' ? webp : await paraBlob(c, 'image/jpeg', q);
    if (!b) break;
    ultimo = b;
    if (b.size <= alvo) return b;
  }
  if (!ultimo) throw new Error('canvas sem saída');
  return ultimo;
}

export interface ImagemCarregada {
  fonte: CanvasImageSource;
  largura: number;
  altura: number;
  url: string;
  liberar: () => void;
}

/** Abre o arquivo escolhido para o recorte (endereço local `blob:`; nada sai do navegador antes de salvar). */
export async function carregarImagem(arquivo: Blob): Promise<ImagemCarregada> {
  const url = URL.createObjectURL(arquivo);
  try {
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
    await img.decode();
    return { fonte: img, largura: img.naturalWidth, altura: img.naturalHeight, url, liberar: () => URL.revokeObjectURL(url) };
  } catch (e) {
    URL.revokeObjectURL(url);
    throw e;
  }
}

export interface ResultadoRecorte {
  blob: Blob;
  largura: number;
  altura: number;
}

export function RecorteImagem({
  imagem,
  modo,
  aoConcluir,
  aoCancelar,
  exportar = exportarRecorte,
}: {
  imagem: ImagemCarregada;
  modo: ModoRecorte;
  aoConcluir: (r: ResultadoRecorte) => void;
  aoCancelar: () => void;
  exportar?: typeof exportarRecorte;
}) {
  const { t: tAdmin } = useTranslation('admin');
  const { t: tCore } = useTranslation();
  // A foto do perfil mora em Minha conta (textos do núcleo); o fundo e a logo, no Admin.
  const t = (chave: string, opcoes?: Record<string, unknown>) => (modo === 'avatar' ? tCore(`conta.avatar.recorte.${chave}`, opcoes) : tAdmin(`telaEntrada.recorte.${chave}`, opcoes));
  const { largura: W, altura: H } = imagem;
  const [proporcao, setProporcao] = useState(() => (modo === 'fundo' ? PROPORCAO_FUNDO : modo === 'avatar' ? PROPORCAO_AVATAR : limitar(W / H, PROPORCAO_LOGO.min, PROPORCAO_LOGO.max)));
  const [zoom, setZoom] = useState(1);
  const [recorte, setRecorte] = useState<Recorte>(() => recorteInicial(W, H, proporcao));
  const [erro, setErro] = useState<string | null>(null);
  const [gerando, setGerando] = useState(false);
  const quadro = useRef<HTMLDivElement>(null);
  const arrasto = useRef<{ x: number; y: number; id: number } | null>(null);
  const [larguraQuadro, setLarguraQuadro] = useState(640);

  useEffect(() => {
    const el = quadro.current;
    if (!el) return;
    const medir = () => el.clientWidth > 0 && setLarguraQuadro(el.clientWidth);
    medir();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const mudarZoom = useCallback((z: number) => {
    const novo = limitar(z, 1, ZOOM_MAX);
    setZoom(novo);
    setRecorte((r) => recorteComZoom(r, W, H, proporcao, novo));
  }, [W, H, proporcao]);
  const mudarProporcao = (p: number) => {
    const nova = limitar(p, PROPORCAO_LOGO.min, PROPORCAO_LOGO.max);
    setProporcao(nova);
    setRecorte((r) => recorteComZoom(r, W, H, nova, zoom));
  };
  /** Pixels da tela para pixels da imagem (o quadro mostra `recorte.largura` px da imagem em `larguraQuadro` px). */
  const escala = recorte.largura / larguraQuadro;
  const mover = (dxTela: number, dyTela: number) => setRecorte((r) => moverRecorte(r, dxTela * escala, dyTela * escala, W, H));

  const aoApertar = (e: PointerEventReact<HTMLDivElement>) => {
    arrasto.current = { x: e.clientX, y: e.clientY, id: e.pointerId };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const aoMover = (e: PointerEventReact<HTMLDivElement>) => {
    const a = arrasto.current;
    if (!a || a.id !== e.pointerId) return;
    // Arrastar a imagem para a direita mostra o que está à esquerda: o recorte anda ao contrário.
    mover(a.x - e.clientX, a.y - e.clientY);
    arrasto.current = { ...a, x: e.clientX, y: e.clientY };
  };
  const aoSoltar = () => (arrasto.current = null);
  const aoTeclar = (e: KeyboardEvent<HTMLDivElement>) => {
    const passo = e.shiftKey ? 40 : 10;
    const acoes: Record<string, () => void> = {
      ArrowLeft: () => mover(-passo, 0),
      ArrowRight: () => mover(passo, 0),
      ArrowUp: () => mover(0, -passo),
      ArrowDown: () => mover(0, passo),
      '+': () => mudarZoom(zoom + 0.1),
      '=': () => mudarZoom(zoom + 0.1),
      '-': () => mudarZoom(zoom - 0.1),
    };
    const f = acoes[e.key];
    if (!f) return;
    e.preventDefault();
    f();
  };
  const aoRolar = (e: WheelEvent<HTMLDivElement>) => mudarZoom(zoom - Math.sign(e.deltaY) * 0.1);

  const saida = tamanhoDaSaida(modo, recorte);
  const usar = async () => {
    setErro(null);
    setGerando(true);
    try {
      const blob = await exportar(imagem.fonte, recorte, modo);
      aoConcluir({ blob, largura: saida.largura, altura: saida.altura });
    } catch {
      setErro(t('falhou'));
    } finally {
      setGerando(false);
    }
  };

  const k = larguraQuadro / recorte.largura;
  return (
    <div className="flex min-w-0 flex-col gap-3" data-recorte={modo}>
      <div
        ref={quadro}
        role="group"
        tabIndex={0}
        aria-label={t('area')}
        aria-describedby={`recorte-${modo}-ajuda`}
        onPointerDown={aoApertar}
        onPointerMove={aoMover}
        onPointerUp={aoSoltar}
        onPointerCancel={aoSoltar}
        onKeyDown={aoTeclar}
        onWheel={aoRolar}
        style={{ aspectRatio: String(proporcao), touchAction: 'none' }}
        className={`relative w-full cursor-grab touch-none overflow-hidden rounded-lg border border-borda bg-sup-2 select-none active:cursor-grabbing ${modo === 'avatar' ? 'max-w-sm self-center' : ''}`}
        data-recorte-quadro
        data-proporcao={proporcao.toFixed(2)}
      >
        <img
          src={imagem.url}
          alt=""
          draggable={false}
          className="pointer-events-none absolute top-0 left-0 max-w-none origin-top-left"
          style={{ width: W * k, height: H * k, transform: `translate(${-recorte.x * k}px, ${-recorte.y * k}px)` }}
          data-recorte-imagem
        />
        {modo === 'avatar' && (
          // Guia circular do tamanho da moldura (rl30-2 VIS-B1): só ajuda a centralizar; o que fica fora do círculo continua na foto.
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 rounded-full border-2 border-foco"
            style={{ boxShadow: '0 0 0 100vmax color-mix(in srgb, var(--fundo) 55%, transparent)' }}
            data-recorte-guia
          />
        )}
      </div>
      <p id={`recorte-${modo}-ajuda`} className="text-xs text-tinta-3">
        {t('ajuda')}
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="flex min-w-0 flex-col gap-1 text-sm text-tinta-2">
          {t('zoom')}
          <input type="range" min={1} max={ZOOM_MAX} step={0.01} value={zoom} onChange={(e) => mudarZoom(Number(e.target.value))} data-recorte-zoom className="w-full accent-acento" />
        </label>
        {modo === 'logo' && (
          <label className="flex min-w-0 flex-col gap-1 text-sm text-tinta-2">
            {t('proporcao', { valor: proporcao.toFixed(1) })}
            <input type="range" min={PROPORCAO_LOGO.min} max={PROPORCAO_LOGO.max} step={0.1} value={proporcao} onChange={(e) => mudarProporcao(Number(e.target.value))} data-recorte-proporcao className="w-full accent-acento" />
          </label>
        )}
      </div>
      <p className="text-sm text-tinta-2" data-recorte-saida>
        {t('saida', { largura: saida.largura, altura: saida.altura })}
      </p>
      {erro && (
        <p role="alert" className="text-sm text-perigo">
          {erro}
        </p>
      )}
      <div className="flex flex-wrap justify-end gap-2">
        <Botao variante="fantasma" icone={<X className="size-4" aria-hidden />} onClick={aoCancelar}>
          {t('cancelar')}
        </Botao>
        <Botao icone={<Check className="size-4" aria-hidden />} carregando={gerando} onClick={() => void usar()} data-recorte-usar>
          {t('usar')}
        </Botao>
      </div>
    </div>
  );
}
